/**
 * Sắp xếp danh sách đơn hàng — thực hiện phía SERVER.
 *
 * URL giữ `sortField` (khoá cột) + `sortOrder` (`ascend`/`descend`) đúng theo chuẩn
 * `useListPageState`, còn API `GET /orders` lại nhận một chuỗi `sort=<field>:<asc|desc>`.
 * File này là nơi DUY NHẤT dịch giữa hai dạng đó, để không sinh thêm một cơ chế sort
 * thứ hai song song với cơ chế đã có của repo.
 */

/**
 * Khoá cột trên bảng → tên field mà API chấp nhận.
 * API chỉ nhận `created_at`, `order_date`, `state`, `total`; field lạ sẽ bị bỏ qua và
 * backend trả về thứ tự mặc định, nên cột nào không có trong bảng này thì không cho sắp xếp.
 */
export const ORDER_SORT_FIELD_MAP: Record<string, string> = {
  order_date: 'order_date',
  created_at: 'created_at',
  total_amount: 'total',
};

/** Chưa chọn sort → ngày đang hiển thị trên bảng mới nhất lên trước. */
export const DEFAULT_ORDER_SORT = { field: 'created_at', order: 'descend' } as const;

export interface OrderSortState {
  /** Khoá cột đang sắp xếp (không phải tên field của API). */
  field: string;
  order: 'ascend' | 'descend';
}

/**
 * Đọc sort từ state của `useListPageState`. Khoá cột không sắp xếp được (hoặc URL trống)
 * đều rơi về mặc định, nên bảng không bao giờ ở trạng thái "thứ tự không xác định".
 */
export function resolveOrderSort(
  sortField?: string,
  sortOrder?: 'ascend' | 'descend',
): OrderSortState {
  if (!sortField || !ORDER_SORT_FIELD_MAP[sortField]) return { ...DEFAULT_ORDER_SORT };
  return { field: sortField, order: sortOrder ?? 'descend' };
}

/** Mũi tên sắp xếp hiển thị trên header cột — chỉ cột đang active mới có. */
export function getColumnSortOrder(
  columnKey: string,
  active: OrderSortState,
): 'ascend' | 'descend' | undefined {
  if (!ORDER_SORT_FIELD_MAP[columnKey]) return undefined;
  return active.field === columnKey ? active.order : undefined;
}

/** Giá trị tham số `sort` gửi lên `GET /orders`, dạng `<field>:<asc|desc>`. */
export function buildOrderSortParam(active: OrderSortState): string {
  const field = ORDER_SORT_FIELD_MAP[active.field] ?? ORDER_SORT_FIELD_MAP[DEFAULT_ORDER_SORT.field];
  return `${field}:${active.order === 'ascend' ? 'asc' : 'desc'}`;
}
